import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import isDarkColor from 'is-dark-color';
import React, { createRef, useEffect, useMemo, useState } from 'react';
import Http from '../Http';
import DashboardBox from '../components/DashboardBox';
import EditButton from '../components/button/EditButton';
import OutlinedIconButton from '../components/button/OutlinedIconButton';
import EditStudentDialog from '../components/dialog/EditStudentDialog';
import SearchBar from '../components/search/SearchBar';
import ApiTable from '../components/table/ApiTable';
import { getFullname } from '../constants/Functionality';
import StudentUtil from '../utils/StudentUtil';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ContainedIconButton from '../components/button/ContainedIconButton';
import ConfirmDialog from '../components/dialog/ConfirmDialog';
import { useUserInfo } from '../providers/UserProvider';
import { AUTHORIZED_SETTINGS_USER_ROLES } from '../constants/Roles';

const StudentApp = ({ student, setSelectedStudent, themeColor }) => {
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [screen, setScreen] = useState(null);

  useEffect(() => {
    setLoading(true);
    StudentUtil.getUserIdByStudyspaceUserId(student.userId)
      .then(response => {
        setUserId(response.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [student.userId]);

  /**
   * This function is responsible for calculating the accent color of a given color
   * @param {String} color Hex color including the leading #
   * @param {Number} amount
   * @returns The accent color of the given color
   */
  function getColorAccent(color, amount) {
    return (
      '#' +
      color
        .replace(/^#/, '')
        .replace(/../g, color =>
          ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2),
        )
    );
  }

  const excludedColors = {
    override: {
      '#EE7A65': true,
    },
  };

  const colorAccent = getColorAccent(themeColor, isDarkColor(themeColor, excludedColors) ? 50 : -50);

  return (
    <DashboardBox
      sx={{
        marginLeft: '10px',
        height: '90vh',
        width: '130vh',
        padding: '0px',
      }}
    >
      <Stack direction="row" justifyContent="space-between" p={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <p className="title">
            <span style={{ color: '#0993D4' }}>
              {getFullname(student.firstName, student.insertion, student.lastName)}
            </span>
          </p>

          <Button
            variant="outlined"
            sx={{
              color: screen == null && isDarkColor(themeColor, excludedColors) ? 'white' : 'black',
              height: '35px',
              width: '135px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 'normal',
              backgroundColor: screen == null ? themeColor : 'white',
              ':hover': {
                backgroundColor: colorAccent,
                color: isDarkColor(themeColor, excludedColors) ? 'white' : 'black',
              },
            }}
            onClick={() => setScreen(null)}
          >
            Planner
          </Button>
          <Button
            variant="outlined"
            sx={{
              color: screen === 'tasks' && isDarkColor(themeColor, excludedColors) ? 'white' : 'black',
              height: '35px',
              width: '135px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 'normal',
              backgroundColor: screen === 'tasks' ? themeColor : 'white',
              ':hover': {
                backgroundColor: colorAccent,
                color: isDarkColor(themeColor, excludedColors) ? 'white' : 'black',
              },
            }}
            onClick={() => setScreen('tasks')}
          >
            Taken
          </Button>

          {loading && <CircularProgress size={16} />}
        </Stack>

        <p>
          <OutlinedIconButton onClick={() => setSelectedStudent(null)}>
            <CloseIcon />
          </OutlinedIconButton>
        </p>
      </Stack>

      {!loading && userId !== null && (
        <iframe
          title="app"
          height="100%"
          style={{ border: 0 }}
          src={
            process.env.REACT_APP_STUDYPLANNER_URL +
            '?auth=' +
            Http.getAccessToken() +
            '&refresh=' +
            Http.getRefreshToken() +
            '&studentId=' +
            userId +
            '&screen=' +
            screen
          }
        />
      )}
    </DashboardBox>
  );
};
export default function Students({ themeColor }) {
  const tableRef = createRef();
  const { userInfo } = useUserInfo();
  const [studentToEdit, setStudentToEdit] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState();
  const [selectedStudent, setSelectedStudent] = useState();
  const filters = useMemo(
    () => ({
      search: searchTerm,
    }),
    [searchTerm],
  );

  const deleteStudent = student => {
    return StudentUtil.deleteStudent(student.id).then(() => {
      tableRef.current?.loadData();
    });
  };

  const columns = useMemo(() => {
    return [
      {
        header: 'Naam',
        enableSortBy: true,
        sortField: 'fullName',
        Cell: ({ value }) => {
          return (
            <Box sx={{ cursor: 'pointer' }} onClick={() => setSelectedStudent(value)}>
              {value.firstName + ' ' + value.lastName}
            </Box>
          );
        },
      },
      {
        header: 'Klas',
        accessor: 'class',
        enableSortBy: true,
        sortField: 'class',
      },
      {
        header: 'Laatste log-in',
        accessor: 'lastLogin',
        enableSortBy: true,
        sortField: 'lastLogin',
      },
      {
        header: 'Acties',
        Cell: ({ value }) => (
          <Stack direction="row">
            <EditButton onClick={() => setStudentToEdit(value)} />
            {userInfo.user.role in AUTHORIZED_SETTINGS_USER_ROLES && (
              <ContainedIconButton color="error" onClick={() => setStudentToDelete(value)}>
                <DeleteIcon />
              </ContainedIconButton>
            )}
          </Stack>
        ),
      },
    ];
  }, [userInfo.user.role]);

  return (
    <Box style={{ display: 'inline-flex', width: '100%' }}>
      <DashboardBox sx={{ width: selectedStudent ? '20%' : null }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <p className="title">Overzicht</p>
            {loading && <CircularProgress size={16} />}
          </Stack>
          <SearchBar setSearchTerm={setSearchTerm} />
        </Stack>
        <ApiTable
          ref={tableRef}
          apiSource={StudentUtil.getStudents}
          columns={columns}
          filters={filters}
          defaultOrderBy="fullName"
          setLoading={setLoading}
        />
      </DashboardBox>
      {selectedStudent != null && (
        <StudentApp themeColor={themeColor} student={selectedStudent} setSelectedStudent={setSelectedStudent} />
      )}

      {studentToEdit !== null && (
        <EditStudentDialog
          student={studentToEdit}
          open={true}
          onClose={(reload = false) => {
            setStudentToEdit(null);
            if (reload) {
              tableRef.current?.loadData();
            }
          }}
        />
      )}

      {studentToDelete !== null && (
        <ConfirmDialog
          title="Leerling verwijderen"
          content={`Weet u zeker dat u ${studentToDelete.firstName} ${studentToDelete.lastName} wilt verwijderen?`}
          confirmText="Verwijderen"
          open={true}
          onConfirm={() => deleteStudent(studentToDelete)}
          onClose={() => setStudentToDelete(null)}
        />
      )}
    </Box>
  );
}
